import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { CustomHeading } from 'shared-components';
import { ApplicationState } from '../../../../../store';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';

import { useNavigate } from 'react-router-dom';
import { FirstApplicationStatus, ReferralSource } from 'api-paths-cas';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function Referral() {
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  const { nextRoute, previousRoute } = useTaskNavigation();

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/referral');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);
  const {
    firstApplicationStatus,
    ucasId,
    referralSource,
    studentReferrerName,
    previousApplicationOrStudentNumber,
    previousStudentNumber,
  } = current;

  const {
    firstApplicationStatus: initialFirstApplicationStatus,
    ucasId: initialUcasId,
    referralSource: initialReferralSource,
    studentReferrerName: initialStudentReferrerName,
    previousApplicationOrStudentNumber: initialPreviousApplicationOrStudentNumber,
    previousStudentNumber: initialPreviousStudentNumber,
  } = initial;

  const navigate = useNavigate();

  const getApplicationStatusLabel = (status: FirstApplicationStatus) => {
    if (status === FirstApplicationStatus.FirstTime) {
      return 'Yes, this is my first time applying';
    } else if (status === FirstApplicationStatus.AppliedOnly) {
      return 'No, I have applied before but not studied';
    }
    return 'No, I have applied and studied before';
  };

  const applicationStatusLabel = getApplicationStatusLabel(firstApplicationStatus);
  const initialApplicationStatusLabel = getApplicationStatusLabel(initialFirstApplicationStatus);

  const showApplicationNumber = previousApplicationOrStudentNumber || initialPreviousApplicationOrStudentNumber;

  const showStudentNumber = previousStudentNumber || initialPreviousStudentNumber;

  const showUcasId = ucasId || initialUcasId;

  const showReferralFields =
    firstApplicationStatus !== FirstApplicationStatus.StudiedBefore ||
    initialFirstApplicationStatus !== FirstApplicationStatus.StudiedBefore;

  const showReferralSource = showReferralFields && (referralSource || initialReferralSource);

  const showStudentReferrerName =
    (referralSource === ReferralSource.UniversityStudent && studentReferrerName) ||
    (initialReferralSource === ReferralSource.UniversityStudent && initialStudentReferrerName);

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.REFERRAL} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          <Field
            leftLabel={`Previously applied`}
            value={applicationStatusLabel}
            originalValue={initialApplicationStatusLabel}
          />

          {showApplicationNumber && (
            <Field
              leftLabel={'Application number'}
              value={previousApplicationOrStudentNumber}
              originalValue={initialPreviousApplicationOrStudentNumber}
            />
          )}

          {showStudentNumber && (
            <Field
              leftLabel={'Student number'}
              value={previousStudentNumber}
              originalValue={initialPreviousStudentNumber}
            />
          )}

          {showUcasId && <Field leftLabel={'UCAS ID'} value={ucasId} originalValue={initialUcasId} />}

          {showReferralSource && (
            <Field
              leftLabel={`Heard about us`}
              value={referralSourceLabels[referralSource]}
              originalValue={referralSourceLabels[initialReferralSource]}
            />
          )}

          {showStudentReferrerName && (
            <Field
              leftLabel={`Referral student's name`}
              value={studentReferrerName}
              originalValue={initialStudentReferrerName}
            />
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={() => {
              // Use dynamic navigation, but fallback to legacy logic if needed
              if (nextRoute) {
                navigate(nextRoute);
              } else {
                // Fallback to legacy logic for declarations task
                const nextSection = applyConfig?.disableDeclarationTask
                  ? '../information-processing'
                  : '../declarations';
                navigate(nextSection);
              }
            }}
          />
        </Box>
      </Box>
    </>
  );
}

const referralSourceLabels: Record<ReferralSource, string> = {
  [ReferralSource.Agent]: 'Agent',
  [ReferralSource.CampusVisit]: 'Campus visit',
  [ReferralSource.EducationFair]: 'Education fair or event',
  [ReferralSource.FamilyMember]: 'Family member',
  [ReferralSource.Friend]: 'Friend',
  [ReferralSource.OnlineSearch]: 'Online search (e.g., Google)',
  [ReferralSource.SchoolCounselor]: 'School counsellor',
  [ReferralSource.SocialMedia]: 'Social media (e.g., Facebook, Instagram, Google Ads)',
  [ReferralSource.Teacher]: 'Teacher',
  [ReferralSource.Ucas]: 'UCAS',
  [ReferralSource.UniversityStudent]: 'University student',
  [ReferralSource.UniversityWebsite]: 'University website',
  [ReferralSource.Other]: 'Other',
  [ReferralSource.PreferNotToSay]: 'Prefer not to say',
};
